import React, { useState, useEffect } from 'react';
import CollapsibleSection from './CollapsibleSection';
import { getSleepThresholds, updateSleepThresholds } from '../services/api';

interface SleepThresholdsProps {
  patientId: string;
}

interface ThresholdData {
  total_sleep_min: number;
  total_sleep_target: number;
  total_sleep_max: number;
  deep_sleep_min: number;
  deep_sleep_target: number;
  deep_sleep_max: number;
  rem_sleep_min: number;
  rem_sleep_target: number;
  rem_sleep_max: number;
  bed_exits_min: number;
  bed_exits_target: number;
  bed_exits_max: number;
  sleep_score_min: number;
  sleep_score_target: number;
  avg_hr_min: number;
  avg_hr_target: number;
  avg_hr_max: number;
  avg_rr_min: number;
  avg_rr_target: number;
  avg_rr_max: number;
  notes?: string;
}

const SleepThresholds: React.FC<SleepThresholdsProps> = ({ patientId }) => {
  const [thresholds, setThresholds] = useState<ThresholdData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadThresholds();
  }, [patientId]);

  const loadThresholds = async () => {
    try {
      const data = await getSleepThresholds(patientId);
      if (data) {
        setThresholds(data);
      } else {
        // Set geriatric defaults if none exist
        setThresholds({
          total_sleep_min: 19800,  // 5.5 hours
          total_sleep_target: 25200,  // 7 hours
          total_sleep_max: 32400,  // 9 hours
          deep_sleep_min: 9000,  // 2.5 hours
          deep_sleep_target: 10800,  // 3 hours
          deep_sleep_max: 14400,  // 4 hours
          rem_sleep_min: 3600,  // 1 hour
          rem_sleep_target: 5400,  // 1.5 hours
          rem_sleep_max: 7200,  // 2 hours
          bed_exits_min: 0,
          bed_exits_target: 0,
          bed_exits_max: 3,
          sleep_score_min: 50,
          sleep_score_target: 70,
          avg_hr_min: 55.0,
          avg_hr_target: 70.0,
          avg_hr_max: 85.0,
          avg_rr_min: 14.0,
          avg_rr_target: 18.0,
          avg_rr_max: 22.0,
          notes: 'Geriatric sleep thresholds adjusted for elderly patients'
        });
      }
    } catch (error) {
      console.error('Error loading sleep thresholds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!thresholds) return;

    setSaving(true);
    try {
      await updateSleepThresholds(patientId, thresholds);
      setEditing(false);
    } catch (error) {
      console.error('Error saving sleep thresholds:', error);
      alert('Failed to save sleep thresholds');
    } finally {
      setSaving(false);
    }
  };

  const formatHours = (seconds: number) => {
    return (seconds / 3600).toFixed(1);
  };

  const updateField = (field: keyof ThresholdData, value: number) => {
    if (!thresholds) return;
    setThresholds({ ...thresholds, [field]: value });
  };

  if (loading) {
    return <div className="text-gray-500">Loading sleep thresholds...</div>;
  }

  if (!thresholds) {
    return <div className="text-red-500">Failed to load sleep thresholds</div>;
  }

  return (
    <CollapsibleSection
      title="Sleep Quality Thresholds"
      icon="🌙"
      actions={
        <>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Edit Thresholds
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setEditing(false);
                loadThresholds();
              }}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
        </>
      }
    >
      <div className="space-y-6">
        {/* Total Sleep */}
        <div className="border-b pb-4">
          <div className="font-medium text-gray-700 mb-3">Total Sleep Duration</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Minimum (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formatHours(thresholds.total_sleep_min)}
                  onChange={(e) => updateField('total_sleep_min', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{formatHours(thresholds.total_sleep_min)} hrs</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Target (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formatHours(thresholds.total_sleep_target)}
                  onChange={(e) => updateField('total_sleep_target', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-green-600 font-medium mt-1">{formatHours(thresholds.total_sleep_target)} hrs</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Maximum (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formatHours(thresholds.total_sleep_max)}
                  onChange={(e) => updateField('total_sleep_max', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{formatHours(thresholds.total_sleep_max)} hrs</div>
              )}
            </div>
          </div>
        </div>

        {/* Deep Sleep */}
        <div className="border-b pb-4">
          <div className="font-medium text-gray-700 mb-3">Deep Sleep Duration</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Minimum (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formatHours(thresholds.deep_sleep_min)}
                  onChange={(e) => updateField('deep_sleep_min', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{formatHours(thresholds.deep_sleep_min)} hrs</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Target (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formatHours(thresholds.deep_sleep_target)}
                  onChange={(e) => updateField('deep_sleep_target', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-green-600 font-medium mt-1">{formatHours(thresholds.deep_sleep_target)} hrs</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Maximum (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formatHours(thresholds.deep_sleep_max)}
                  onChange={(e) => updateField('deep_sleep_max', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{formatHours(thresholds.deep_sleep_max)} hrs</div>
              )}
            </div>
          </div>
        </div>

        {/* REM Sleep */}
        <div className="border-b pb-4">
          <div className="font-medium text-gray-700 mb-3">REM Sleep Duration</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Minimum (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formatHours(thresholds.rem_sleep_min)}
                  onChange={(e) => updateField('rem_sleep_min', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{formatHours(thresholds.rem_sleep_min)} hrs</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Target (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formatHours(thresholds.rem_sleep_target)}
                  onChange={(e) => updateField('rem_sleep_target', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-green-600 font-medium mt-1">{formatHours(thresholds.rem_sleep_target)} hrs</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Maximum (hours)</label>
              {editing ? (
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formatHours(thresholds.rem_sleep_max)}
                  onChange={(e) => updateField('rem_sleep_max', parseFloat(e.target.value) * 3600)}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{formatHours(thresholds.rem_sleep_max)} hrs</div>
              )}
            </div>
          </div>
        </div>

        {/* Bed Exits */}
        <div className="border-b pb-4">
          <div className="font-medium text-gray-700 mb-3">Bed Exits per Night</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">Minimum</label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={thresholds.bed_exits_min}
                  onChange={(e) => updateField('bed_exits_min', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{thresholds.bed_exits_min}</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Target</label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={thresholds.bed_exits_target}
                  onChange={(e) => updateField('bed_exits_target', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-green-600 font-medium mt-1">{thresholds.bed_exits_target}</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Maximum (Alert)</label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  value={thresholds.bed_exits_max}
                  onChange={(e) => updateField('bed_exits_max', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-red-600 font-medium mt-1">{thresholds.bed_exits_max}</div>
              )}
            </div>
          </div>
        </div>

        {/* Sleep Score */}
        <div className="border-b pb-4">
          <div className="font-medium text-gray-700 mb-3">Sleep Score</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Minimum Acceptable</label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.sleep_score_min}
                  onChange={(e) => updateField('sleep_score_min', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-gray-900 font-medium mt-1">{thresholds.sleep_score_min}</div>
              )}
            </div>
            <div>
              <label className="text-sm text-gray-600">Target Score</label>
              {editing ? (
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={thresholds.sleep_score_target}
                  onChange={(e) => updateField('sleep_score_target', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded px-2 py-1 mt-1"
                />
              ) : (
                <div className="text-green-600 font-medium mt-1">{thresholds.sleep_score_target}</div>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {thresholds.notes && !editing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm font-medium text-blue-900 mb-1">Notes:</div>
            <div className="text-sm text-blue-800">{thresholds.notes}</div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default SleepThresholds;
